import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../api';
import { User } from '../types';
import Sidebar from '../components/Sidebar';
import './UserList.css';

const UserList: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [blockingUserId, setBlockingUserId] = useState<number | null>(null);

  useEffect(() => {
    loadUsers();
  }, [currentPage, pageSize]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const paginatedData = await userService.getPaginatedUsers({
        pageNo: currentPage,
        pageSize: pageSize,
      });
      setUsers(paginatedData.data || []);
      setTotalPages(paginatedData.pages || 0);
      setTotalElements(paginatedData.total || 0);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockToggle = async (userId: number) => {
    try {
      setBlockingUserId(userId);
      await userService.blockUser(userId);

      // Update the user's blocked status in the local state
      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, blocked: !user.blocked }
          : user
      ));
    } catch (error) {
      console.error('Error toggling user block status:', error);
      alert('Failed to update user block status. Please try again.');
    } finally {
      setBlockingUserId(null);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="layout">
      <Sidebar />
      <div className="main-content">
        <div className="top-bar">
          <div className="user-info">
            <span className="user-icon">👤</span>
            <span className="user-email">{currentUser?.email || 'admin@roameez.com'}</span>
          </div>
        </div>

        <div className="page-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">Users ({totalElements})</h1>
              <div className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">›</span>
                <span>Users</span>
              </div>
            </div>
            <div className="page-actions">
              <select
                className="page-size-select"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value="10">10 per page</option>
                <option value="20">20 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">Loading...</div>
          ) : users && users.length > 0 ? (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Profile</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Username</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>
                          {user.profilePic ? (
                            <img
                              src={user.profilePic}
                              alt={user.name}
                              className="user-avatar"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                if (placeholder) placeholder.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="user-avatar-placeholder"
                            style={{ display: user.profilePic ? 'none' : 'flex' }}
                          >
                            {user.name?.charAt(0) || '?'}
                          </div>
                        </td>
                        <td className="user-name-cell">{user.name}</td>
                        <td>{user.email || '-'}</td>
                        <td>
                          {user.phone ? `${user.countryCode || ''} ${user.phone}` : '-'}
                        </td>
                        <td>{user.username ? `@${user.username}` : '-'}</td>
                        <td>
                          <span className={`status-badge ${user.blocked ? 'blocked' : 'active'}`}>
                            {user.blocked ? 'Blocked' : 'Active'}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`toggle-btn ${user.blocked ? 'blocked' : 'active'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBlockToggle(user.id);
                            }}
                            disabled={blockingUserId === user.id}
                          >
                            {blockingUserId === user.id ? (
                              'Processing...'
                            ) : user.blocked ? (
                              'Unblock'
                            ) : (
                              'Block'
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-data">
              <p>No users found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserList;
